import type { PanelTable } from '../../../queryV5/types';
import { DEFAULT_FIELDS, edgeTone } from './graphMetrics';
import type {
	GraphData,
	GraphEdge,
	GraphFields,
	GraphNode,
	GraphSettings,
} from './types';

function text(value: unknown): string {
	if (typeof value !== 'string') {
		return '';
	}
	return value.trim();
}

function nonnegative(value: unknown): number | null {
	if (value === null || value === undefined || value === '') {
		return null;
	}
	if (typeof value !== 'number' && typeof value !== 'string') {
		return null;
	}
	const number = Number(value);
	if (!Number.isFinite(number) || number < 0) {
		return null;
	}
	return number;
}

function failingTrace(value: unknown, errors: number | null): string | null {
	const id = text(value);
	if (!errors || !/^[a-f\d]{32}$/i.test(id) || /^0{32}$/.test(id)) {
		return null;
	}
	return id;
}

function readEdge(
	read: (key: keyof GraphFields) => unknown,
	settings: GraphSettings,
): GraphEdge | string {
	const sourceName = text(read('source'));
	const targetName = text(read('target'));
	if (!sourceName || !targetName) {
		return 'Every connection needs non-empty source and target names.';
	}
	const scope = text(read('scope'));
	const source = JSON.stringify([scope, sourceName]);
	const target = JSON.stringify([scope, targetName]);
	const id = JSON.stringify([scope, sourceName, targetName]);
	const calls = nonnegative(read('calls'));
	const errors = nonnegative(read('errors'));
	if (calls !== null && errors !== null && errors > calls) {
		return 'Failed calls cannot exceed total calls. Check the query and field mappings.';
	}
	let rate: number | null = null;
	if (calls !== null && calls > 0 && errors !== null) {
		rate = (100 * errors) / calls;
	}
	return {
		id,
		source,
		target,
		sourceName,
		targetName,
		scope,
		calls,
		errors,
		rate,
		latency: nonnegative(read('latency')),
		traceId: failingTrace(read('traceId'), errors),
		tone: edgeTone(rate, settings),
	};
}

export function readGraphTable(
	table: PanelTable,
	settings: GraphSettings,
): GraphData {
	const nodes: GraphNode[] = [];
	const edges: GraphEdge[] = [];
	if (!table.rows.length) {
		return { status: 'ready', nodes, edges, omitted: 0 };
	}
	const columns = new Map(
		table.columns.map((column) => [column.name, column.id]),
	);
	const field = (key: keyof GraphFields): string =>
		settings.fields?.[key] || DEFAULT_FIELDS[key];
	if (!columns.has(field('source')) || !columns.has(field('target'))) {
		return {
			status: 'invalid',
			message: `Return “${field('source')}” and “${field('target')}” columns, or update the field mappings.`,
		};
	}
	for (const row of table.rows) {
		const read = (key: keyof GraphFields): unknown =>
			row.data[columns.get(field(key)) ?? field(key)];
		const edge = readEdge(read, settings);
		if (typeof edge === 'string') {
			return { status: 'invalid', message: edge };
		}
		nodes.push({
			id: edge.source,
			name: edge.sourceName,
			scope: edge.scope,
			group: text(read('sourceGroup')),
		});
		nodes.push({
			id: edge.target,
			name: edge.targetName,
			scope: edge.scope,
			group: text(read('targetGroup')),
		});
		edges.push(edge);
	}
	return { status: 'ready', nodes, edges, omitted: 0 };
}
